<template>
  <div class="container">
    <h1>Privacy Policy</h1>
    <hr />
    <p>Welcome to Wrytopia.</p>
    <p>
      At Wrytopia, we are dedicated to safeguarding and respecting your privacy.
      This policy pertains to the Wrytopia apps, websites, and associated
      services (the “Platform”). It elaborates on our practices regarding the
      personal data we collect from you or that you provide to us in association
      with the Platform. It does not apply to other products or services that
      may display or reference a different privacy policy. If you do not concur
      with this policy, please refrain from using the Platform.
    </p>
    <p>
      If you have any inquiries regarding how we employ your personal data,
      kindly contact us at
      <a
        href="mailto: team.wrytopia@gmail.com"
        class="link-success"
        target="_blank"
        >team.wrytopia@gmail.com</a
      >.
    </p>

    <ol>
      <div>
        <li class="bold">Information We Collect</li>
        <p>
          When you use Wrytopia, we may gather the following types of personal
          data:
        </p>
        1.1 Personal Information:

        <ul>
          <li>
            Name: We collect your name to enhance your experience and facilitate
            effective communication.
          </li>

          <li>
            Email Address: Your email address is mandatory for creating and
            managing your account, sending crucial notifications, and providing
            customer support.
          </li>

          <li>
            Age: We may collect your age to confirm that you meet the legal age
            requirements for using our services.
          </li>

          <li>
            Google Account Information: If you sign up using your Google
            account, we may collect limited information from your Google
            profile, such as your name and email address, to streamline the
            sign-up process.
          </li>
        </ul>
      </div>

      <div>
        <li class="bold">How We Use Your Information</li>
        <p>
          We utilize your information to furnish you with the Platform and to
          enhance and administer it. In general, we use the various categories
          of information we collect for the following purposes:
        </p>

        <ul>
          <li>To provide Platform services.</li>

          <li>
            To enhance and develop our Platform and engage in product
            development.
          </li>

          <li>
            To assess and understand the effectiveness of the advertising we
            serve to you and others, delivering targeted advertising.
          </li>

          <li>
            To employ User Content in our advertising and marketing campaigns
            promoting the Platform.
          </li>

          <li>
            To infer additional information about you, such as your age, gender,
            and interests, aiding in the detection of abuse, fraud, and illegal
            activity on the Platform.
          </li>

          <li>
            To verify your eligibility to use the Platform, as mandated by law.
          </li>

          <li>To offer customer support and respond to your queries.</li>

          <li>
            To communicate with you, including notifying you about changes in
            our services.
          </li>

          <li>
            To enforce our terms, conditions, and policies, and to meet our
            legal obligations.
          </li>

          <li>
            In line with your permissions, to furnish you with location-based
            services, like advertising and personalized content.
          </li>

          <li>
            To inform our algorithms by combining all the information we collect
            or receive about you for the aforementioned purposes.
          </li>

          <li>
            For any other purposes disclosed to you when we collect your
            information or as per your consent.
          </li>
        </ul>
      </div>

      <div>
        <li class="bold">The Security of Your Personal Data</li>
        <p>
          We implement reasonable and appropriate technical and organizational
          measures to safeguard personal data from unauthorized access, use,
          disclosure, alteration, and destruction. Regrettably, the transmission
          of information over the internet is not entirely secure, and we cannot
          guarantee the security of information transmitted via the Platform.
          Any transmission is at your own risk.
        </p>
      </div>

      <div>
        <li class="bold">How Long We Keep Your Personal Data</li>
        <p>
          We retain your information for as long as it is necessary to provide
          the service or fulfill the transactions you have requested or
          authorized. When we no longer need your information to provide the
          service, we retain it only as long as we have a legitimate business
          purpose in keeping such data, which includes complying with legal
          obligations, enforcing agreements, resolving disputes, and as
          necessary for the establishment, exercise, or defense of legal claims.
        </p>
      </div>

      <div>
        <li class="bold">Information Relating to Children</li>
        <p>
          The Platform is not intended for children under the age of 13 or the
          equivalent minimum age in the relevant jurisdiction. If you believe
          that we have personal data about or collected from a child under the
          relevant age, please contact us at
          <a
            href="mailto: team.wrytopia@gmail.com"
            class="link-success"
            target="_blank"
            >team.wrytopia@gmail.com</a
          >.
        </p>
      </div>

      <div>
        <li class="bold">Complaints</li>
        <p>
          If you wish to file a complaint about how we process your personal
          data, please contact us initially at
          [team.wrytopia@gmail.com](mailto:team.wrytopia@gmail.com), and we will
          strive to address your request as promptly as possible. This is
          without prejudice to your right to file a claim with your data
          protection authority or follow the dispute resolution process
          specified in the Terms of Service.
        </p>
      </div>

      <div>
        <li class="bold">Changes</li>
        <p>
          We will generally notify all users of any significant changes to this
          policy through a notice provided via the Platform. Nevertheless, it is
          advisable to periodically review this policy to check for any changes.
          We will also update the “Last Updated” date at the top of this policy,
          reflecting the effective date of the policy changes. Your continued
          access to or use of the Platform after the updated policy's date
          constitutes your acceptance of the revised policy. If you do not agree
          to the updated policy, kindly cease accessing or using the Platform.
        </p>
      </div>

      <div>
        <li class="bold">Contact</li>
        <p>
          Questions, comments, and requests regarding this policy should be
          addressed to
          <a
            href="mailto: team.wrytopia@gmail.com"
            class="link-success"
            target="_blank"
            >team.wrytopia@gmail.com</a
          >.
        </p>
      </div>
      
      <div>
        <li class="bold">Changes to this Privacy Policy</li>
        <p>
          We may update this Privacy Policy periodically to align with changes
          in our practices or legal requirements. Any updates will be posted on
          our website, and the date at the top of the policy will be amended
          accordingly. We encourage you to review this policy regularly to stay
          informed about how we protect your privacy.
        </p>
        <p>
          Thank you for choosing Wrytopia. We value your trust and are committed
          to safeguarding your personal information.
        </p>
      </div>
    </ol>
  </div>
</template>

<script>
export default{
  name: 'PrivacyPolicyPage'
}
</script>

<style scoped>
.container {
  min-height: 100vh;
  margin-top: 150px;
  text-align: justify;
}

* {
  color: black !important;
  font-family: "Poppins" !important;
  font-weight: 400 !important;
  line-height: 200% !important;
}

strong {
  font-weight: 800 !important;
}

a {
  color: #00b18f !important;
}

.bold {
  font-weight: bold !important;
}
</style>
